import React from 'react';
import { motion } from 'framer-motion';

export type OrbState = 'idle' | 'listening' | 'speaking' | 'thinking';

interface AIOrbProps {
  state?: OrbState;
  size?: number;
  className?: string;
}

const STATE_GRADIENT: Record<OrbState, string> = {
  idle: 'from-primary/80 via-violet-500/70 to-secondary/70',
  listening: 'from-rose-500 via-red-500 to-orange-500',
  speaking: 'from-emerald-400 via-teal-400 to-cyan-500',
  thinking: 'from-primary via-indigo-500 to-secondary',
};

const STATE_GLOW: Record<OrbState, string> = {
  idle: '0 0 25px rgba(108,99,255,0.5)',
  listening: '0 0 30px rgba(244,63,94,0.55)',
  speaking: '0 0 30px rgba(16,185,129,0.55)',
  thinking: '0 0 32px rgba(139,92,246,0.6)',
};

/**
 * Animated AI orb. Rotates and pulses while thinking, radiates ripples while
 * listening or speaking.
 */
const AIOrb: React.FC<AIOrbProps> = ({ state = 'idle', size = 64, className = '' }) => {
  const isActive = state === 'listening' || state === 'speaking';

  return (
    <div
      className={`relative flex items-center justify-center shrink-0 ${className}`}
      style={{ width: size, height: size }}
    >
      {/* Ripple rings */}
      {state === 'listening' && (
        <>
          <span className="absolute inset-0 rounded-full bg-rose-500/40 animate-ripple" />
          <span className="absolute inset-0 rounded-full bg-rose-500/30 animate-ripple" style={{ animationDelay: '0.6s' }} />
        </>
      )}
      {state === 'speaking' && (
        <>
          <span className="absolute inset-0 rounded-full bg-emerald-400/40 animate-ripple" />
          <span className="absolute inset-0 rounded-full bg-emerald-400/30 animate-ripple" style={{ animationDelay: '0.6s' }} />
        </>
      )}
      {state === 'thinking' && (
        <span className="absolute inset-0 rounded-full bg-primary/30 animate-orb-pulse" />
      )}

      {/* Orb body */}
      <motion.div
        animate={state === 'thinking' ? { rotate: 360 } : { rotate: [0, 8, -8, 0] }}
        transition={
          state === 'thinking'
            ? { duration: 5, repeat: Infinity, ease: 'linear' }
            : { duration: 4, repeat: Infinity, ease: 'easeInOut' }
        }
        className={`w-full h-full rounded-full bg-gradient-to-br ${STATE_GRADIENT[state]} backdrop-blur-md`}
        style={{ boxShadow: STATE_GLOW[state] }}
      >
        <div className="absolute inset-1 rounded-full bg-white/10 backdrop-blur-sm" />
      </motion.div>

      {/* Inner core */}
      <motion.div
        animate={isActive ? { scale: [1, 1.18, 1] } : { scale: [1, 1.08, 1] }}
        transition={{ duration: isActive ? 0.9 : 2.2, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute rounded-full bg-white/25"
        style={{ width: size * 0.5, height: size * 0.5 }}
      />
      <motion.div
        animate={{ scale: [1, 0.85, 1] }}
        transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute rounded-full bg-white/90 shadow-[0_0_12px_rgba(255,255,255,0.9)]"
        style={{ width: size * 0.2, height: size * 0.2 }}
      />
    </div>
  );
};

export default AIOrb;
